import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Modal,
  TextInput,
  Alert,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../contexts/AuthContext';
import { changePassword } from '../services/userService';
import PaddingPreview from '../components/PaddingPreview';
import { getCurrentVersion } from '../services/versionService';

const ProfileScreen = () => {
  const { user, signOut, loading, updatePreferences } = useAuth();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSortingModal, setShowSortingModal] = useState(false);
  const [showPaddingModal, setShowPaddingModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [updatingPreferences, setUpdatingPreferences] = useState(false);
  const [tempPadding, setTempPadding] = useState(12);
  const [tempInnerPadding, setTempInnerPadding] = useState(12);

  useEffect(() => {
    if (user) {
      setTempPadding(user.itemPadding || 12);
      setTempInnerPadding(user.itemInnerPadding || 12);
    }
  }, [user]);

  const handleLogout = async () => {
    console.log('🔴 Logout button clicked');
    
    // Für Web: Browser-Confirm verwenden
    if (Platform.OS === 'web') {
      if (window.confirm('Möchtest du dich wirklich abmelden?')) {
        console.log('🔴 Logout confirmed, calling signOut...');
        const result = await signOut();
        console.log('🔴 SignOut result:', result);
      }
    } else {
      // Für Native: Alert verwenden (später implementieren falls nötig)
      console.log('🔴 Logout confirmed, calling signOut...');
      const result = await signOut();
      console.log('🔴 SignOut result:', result);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      Alert.alert('Fehler', 'Bitte fülle alle Felder aus');
      return;
    }

    if (newPassword !== confirmPassword) {
      Alert.alert('Fehler', 'Die neuen Passwörter stimmen nicht überein');
      return;
    }

    if (newPassword.length < 6) {
      Alert.alert('Fehler', 'Das neue Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    setChangingPassword(true);
    try {
      const result = await changePassword(currentPassword, newPassword);
      if (result.success) {
        Alert.alert('Erfolg', 'Passwort wurde erfolgreich geändert');
        setShowPasswordModal(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        Alert.alert('Fehler', result.error);
      }
    } catch (error) {
      Alert.alert('Fehler', 'Fehler beim Ändern des Passworts');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleUpdateSorting = async (sorting) => {
    setUpdatingPreferences(true);
    try {
      const result = await updatePreferences({ shoppingListSorting: sorting });
      if (result.success) {
        setShowSortingModal(false);
        Alert.alert('Erfolg', 'Sortierung wurde aktualisiert');
      } else {
        Alert.alert('Fehler', result.error);
      }
    } catch (error) {
      Alert.alert('Fehler', 'Fehler beim Aktualisieren der Sortierung');
    } finally {
      setUpdatingPreferences(false);
    }
  };

  const handleUpdatePadding = async () => {
    setUpdatingPreferences(true);
    try {
      const result = await updatePreferences({ 
        itemPadding: tempPadding,
        itemInnerPadding: tempInnerPadding 
      });
      if (result.success) {
        setShowPaddingModal(false);
        Alert.alert('Erfolg', 'Einkaufslisten-Dichte wurde aktualisiert');
      } else {
        Alert.alert('Fehler', result.error);
      }
    } catch (error) {
      Alert.alert('Fehler', 'Fehler beim Aktualisieren der Dichte');
    } finally {
      setUpdatingPreferences(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color="#2E7D32" />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <View style={styles.card}>
        <Text style={styles.label}>Name</Text>
        <Text style={styles.value}>
          {user?.firstName} {user?.lastName}
        </Text>
      </View>

      <View style={styles.card}>
        <Text style={styles.label}>E-Mail</Text>
        <Text style={styles.value}>{user?.email}</Text>
      </View>

      <View style={styles.card}>
        <Text style={styles.label}>Familie</Text>
        <Text style={styles.value}>
          {user?.familyName || 'Familie ' + user?.lastName}
        </Text>
      </View>

      {/* Passwort ändern */}
      <TouchableOpacity 
        style={styles.actionCard} 
        onPress={() => setShowPasswordModal(true)}
      >
        <View style={styles.actionContent}>
          <Ionicons name="lock-closed-outline" size={24} color="#2E7D32" />
          <View style={styles.actionText}>
            <Text style={styles.actionTitle}>Passwort ändern</Text>
            <Text style={styles.actionSubtitle}>Sicherheitseinstellungen</Text>
          </View>
          <Ionicons name="chevron-forward" size={20} color="#666" />
        </View>
      </TouchableOpacity>

      {/* Sortierung ändern */}
      <TouchableOpacity 
        style={styles.actionCard} 
        onPress={() => setShowSortingModal(true)}
      >
        <View style={styles.actionContent}>
          <Ionicons name="list-outline" size={24} color="#2E7D32" />
          <View style={styles.actionText}>
            <Text style={styles.actionTitle}>Einkaufslisten-Sortierung</Text>
            <Text style={styles.actionSubtitle}>
              {user?.shoppingListSorting === 'alphabetical' ? 'Alphabetisch' : 'Manuell'}
            </Text>
          </View>
          <Ionicons name="chevron-forward" size={20} color="#666" />
        </View>
      </TouchableOpacity>

      {/* Padding-Einstellung */}
      <TouchableOpacity 
        style={styles.actionCard} 
        onPress={() => {
          setTempPadding(user?.itemPadding || 12);
          setTempInnerPadding(user?.itemInnerPadding || 12);
          setShowPaddingModal(true);
        }}
      >
        <View style={styles.actionContent}>
          <Ionicons name="options-outline" size={24} color="#2E7D32" />
          <View style={styles.actionText}>
            <Text style={styles.actionTitle}>Einkaufslisten-Dichte</Text>
            <Text style={styles.actionSubtitle}>
              {user?.itemPadding || 12}px Abstand, {user?.itemInnerPadding || 12}px Innenabstand
            </Text>
          </View>
          <Ionicons name="chevron-forward" size={20} color="#666" />
        </View>
      </TouchableOpacity>

      <TouchableOpacity style={styles.logoutButton} onPress={handleLogout}>
        <Text style={styles.logoutText}>Abmelden</Text>
      </TouchableOpacity>

      <Text style={styles.version}>Version {getCurrentVersion()}</Text>

      {/* Passwort-Änderungs-Modal */}
      <Modal
        visible={showPasswordModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowPasswordModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Passwort ändern</Text>
            
            <TextInput
              style={styles.input}
              placeholder="Aktuelles Passwort"
              value={currentPassword}
              onChangeText={setCurrentPassword}
              secureTextEntry
              autoCapitalize="none"
            />
            
            <TextInput
              style={styles.input}
              placeholder="Neues Passwort"
              value={newPassword}
              onChangeText={setNewPassword}
              secureTextEntry
              autoCapitalize="none"
            />
            
            <TextInput
              style={styles.input}
              placeholder="Neues Passwort bestätigen"
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              secureTextEntry
              autoCapitalize="none"
            />
            
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => setShowPasswordModal(false)}
              >
                <Text style={styles.cancelButtonText}>Abbrechen</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.modalButton, styles.saveButton]}
                onPress={handleChangePassword}
                disabled={changingPassword}
              >
                {changingPassword ? (
                  <ActivityIndicator color="#fff" size="small" />
                ) : (
                  <Text style={styles.saveButtonText}>Ändern</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Sortierungs-Modal */}
      <Modal
        visible={showSortingModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowSortingModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Einkaufslisten-Sortierung</Text>
            <Text style={styles.modalSubtitle}>
              Wähle, wie deine Einkaufslisten sortiert werden sollen
            </Text>
            
            <TouchableOpacity
              style={[
                styles.sortingOption,
                user?.shoppingListSorting === 'smart' && styles.sortingOptionSelected
              ]}
              onPress={() => handleUpdateSorting('smart')}
              disabled={updatingPreferences}
            >
              <View style={styles.sortingOptionContent}>
                <Ionicons name="bulb-outline" size={24} color="#2E7D32" />
                <View style={styles.sortingOptionText}>
                  <Text style={styles.sortingOptionTitle}>Manuelle Sortierung</Text>
                  <Text style={styles.sortingOptionDescription}>
                    Du bestimmst selbst die Reihenfolge der Artikel durch Verschieben
                  </Text>
                </View>
                {user?.shoppingListSorting === 'smart' && (
                  <Ionicons name="checkmark-circle" size={24} color="#2E7D32" />
                )}
              </View>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[
                styles.sortingOption,
                user?.shoppingListSorting === 'alphabetical' && styles.sortingOptionSelected
              ]}
              onPress={() => handleUpdateSorting('alphabetical')}
              disabled={updatingPreferences}
            >
              <View style={styles.sortingOptionContent}>
                <Ionicons name="list-outline" size={24} color="#2E7D32" />
                <View style={styles.sortingOptionText}>
                  <Text style={styles.sortingOptionTitle}>Alphabetische Sortierung</Text>
                  <Text style={styles.sortingOptionDescription}>
                    Artikel werden alphabetisch sortiert (A-Z)
                  </Text>
                </View>
                {user?.shoppingListSorting === 'alphabetical' && (
                  <Ionicons name="checkmark-circle" size={24} color="#2E7D32" />
                )}
              </View>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[styles.modalButton, styles.cancelButton]}
              onPress={() => setShowSortingModal(false)}
            >
              <Text style={styles.cancelButtonText}>Schließen</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Padding-Modal */}
      <Modal
        visible={showPaddingModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowPaddingModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Einkaufslisten-Dichte</Text>
            <Text style={styles.modalSubtitle}>
              Stelle den Abstand zwischen und innerhalb der Artikel-Kacheln ein
            </Text>
            
            <View style={styles.sliderContainer}>
              <Text style={styles.sliderLabel}>Abstand zwischen Kacheln: {tempPadding}px</Text>
              <View style={styles.sliderTrack}>
                <View style={styles.sliderRange}>
                  <Text style={styles.sliderMin}>0</Text>
                  <Text style={styles.sliderMax}>30</Text>
                </View>
                <View style={styles.sliderWrapper}>
                  <View 
                    style={[
                      styles.sliderFill, 
                      { width: `${(tempPadding / 30) * 100}%` }
                    ]} 
                  />
                  <View 
                    style={[
                      styles.sliderThumb, 
                      { left: `${(tempPadding / 30) * 100}%` }
                    ]} 
                  />
                  <View 
                    style={styles.sliderTouchArea}
                    onTouchStart={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.nativeEvent.touches[0].clientX - rect.left;
                      const percentage = Math.max(0, Math.min(1, x / rect.width));
                      setTempPadding(Math.round(percentage * 30));
                    }}
                    onTouchMove={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.nativeEvent.touches[0].clientX - rect.left;
                      const percentage = Math.max(0, Math.min(1, x / rect.width));
                      setTempPadding(Math.round(percentage * 30));
                    }}
                  />
                </View>
              </View>
            </View>

            <View style={styles.sliderContainer}>
              <Text style={styles.sliderLabel}>Innenabstand der Kacheln: {tempInnerPadding}px</Text>
              <View style={styles.sliderTrack}>
                <View style={styles.sliderRange}>
                  <Text style={styles.sliderMin}>0</Text>
                  <Text style={styles.sliderMax}>24</Text>
                </View>
                <View style={styles.sliderWrapper}>
                  <View 
                    style={[
                      styles.sliderFill, 
                      { width: `${(tempInnerPadding / 24) * 100}%` }
                    ]} 
                  />
                  <View 
                    style={[
                      styles.sliderThumb, 
                      { left: `${(tempInnerPadding / 24) * 100}%` }
                    ]} 
                  />
                  <View 
                    style={styles.sliderTouchArea}
                    onTouchStart={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.nativeEvent.touches[0].clientX - rect.left;
                      const percentage = Math.max(0, Math.min(1, x / rect.width));
                      setTempInnerPadding(Math.round(percentage * 24));
                    }}
                    onTouchMove={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.nativeEvent.touches[0].clientX - rect.left;
                      const percentage = Math.max(0, Math.min(1, x / rect.width));
                      setTempInnerPadding(Math.round(percentage * 24));
                    }}
                  />
                </View>
              </View>
            </View>

            <PaddingPreview padding={tempPadding} innerPadding={tempInnerPadding} />
            
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => setShowPaddingModal(false)}
              >
                <Text style={styles.cancelButtonText}>Abbrechen</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.saveButton]}
                onPress={handleUpdatePadding}
                disabled={updatingPreferences}
              >
                {updatingPreferences ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.saveButtonText}>Speichern</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    padding: 16,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  actionCard: {
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  actionContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionText: {
    flex: 1,
    marginLeft: 15,
  },
  actionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 2,
  },
  actionSubtitle: {
    fontSize: 14,
    color: '#666',
  },
  label: {
    fontSize: 12,
    color: '#666',
    marginBottom: 5,
    textTransform: 'uppercase',
    fontWeight: '600',
  },
  value: {
    fontSize: 16,
    color: '#333',
  },
  logoutButton: {
    backgroundColor: '#d32f2f',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 20,
  },
  logoutText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  version: {
    textAlign: 'center',
    color: '#999',
    marginTop: 30,
    fontSize: 12,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
    textAlign: 'center',
  },
  modalSubtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 20,
    textAlign: 'center',
  },
  input: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    marginBottom: 16,
    backgroundColor: '#f9f9f9',
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
  },
  modalButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginHorizontal: 4,
  },
  cancelButton: {
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  saveButton: {
    backgroundColor: '#2E7D32',
  },
  cancelButtonText: {
    color: '#666',
    fontSize: 16,
    fontWeight: '600',
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  // Sorting options
  sortingOption: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    backgroundColor: '#f9f9f9',
  },
  sortingOptionSelected: {
    borderColor: '#2E7D32',
    backgroundColor: '#f0f8f0',
  },
  sortingOptionContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sortingOptionText: {
    flex: 1,
    marginLeft: 12,
  },
  sortingOptionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  sortingOptionDescription: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  // Padding slider styles
  sliderContainer: {
    marginBottom: 20,
  },
  sliderLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
    textAlign: 'center',
  },
  sliderTrack: {
    position: 'relative',
  },
  sliderRange: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  sliderMin: {
    fontSize: 12,
    color: '#666',
  },
  sliderMax: {
    fontSize: 12,
    color: '#666',
  },
  sliderWrapper: {
    position: 'relative',
    height: 20,
    backgroundColor: '#e0e0e0',
    borderRadius: 10,
    marginBottom: 16,
  },
  sliderFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    backgroundColor: '#2E7D32',
    borderRadius: 10,
  },
  sliderThumb: {
    position: 'absolute',
    top: -6,
    width: 32,
    height: 32,
    backgroundColor: '#2E7D32',
    borderRadius: 16,
    borderWidth: 3,
    borderColor: '#fff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
    transform: [{ translateX: -16 }],
  },
  sliderTouchArea: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
  },
});

export default ProfileScreen;

